import { z } from 'zod'

import { lenientList } from '@/schemas/common'
import { isoDateTimeSchema } from '@/schemas/newsletter'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Mitra's take — `/api/v5/company/{symbol}/insights/…` — in the app's own
 * vocabulary: the short plain-words card mm-be writes ahead of time for each
 * section of the Stock page, the longer explainer behind it, and the 👍/👎 a
 * reader leaves on it.
 *
 * Its own file beside `schemas/company-profile.ts` rather than inside it:
 * those are the tabs' FIGURES, this is TEXT a model wrote about them, from a
 * different mm-be service (`services/insights`) with its own states. The wire
 * shapes live in `@/api/schema` and are never restated here; this file is
 * what the app holds AFTER `insightsService`'s mapper has run.
 *
 * Three rules the file turns on:
 *
 *  1. ONLY `ready` CARRIES TEXT. mm-be never serves an out-of-date take as
 *     current: it answers 200 with `card: null` and a status instead. The
 *     type makes that the only shape — a `ready` take always has a card, and
 *     an `updating` or `unavailable` one never has a card or an explainer —
 *     so a screen narrows on `status` and cannot draw a stale headline by
 *     accident.
 *  2. AN ODD ANSWER IS `unavailable`, NEVER AN ERROR. An unknown status, or a
 *     `ready` whose card is missing or blank, is narrowed to `unavailable` by
 *     the service before this schema runs — "Not available yet", not a failed
 *     block.
 *  3. VOCABULARY IS CLOSED HERE, NOT ON THE WIRE. `status`, `reason` and
 *     `tone` are bare CharFields in mm-be; the service narrows an unknown
 *     `reason` or `tone` to null (`knownWord`) and an unknown `status` to
 *     `unavailable` (rule 2), so a new word costs nothing on screen.
 */

// ── Pages ────────────────────────────────────────────────────────────────────

/**
 * Every page mm-be writes a take for, in its own order (`services/insights/
 * pages.py`) — the order of the list endpoint too. Any other key is a 404
 * `INSIGHTS_UNKNOWN_PAGE`. `ratings` and `index` have no Stock tab today.
 */
export const INSIGHT_PAGES = [
  'overview',
  'technical',
  'events',
  'share_holding',
  'price_history',
  'dividend',
  'agm_history',
  'announcements',
  'ratings',
  'swot',
  'broker_holding',
  'floorsheet',
  'returns',
  'trading',
  'index',
] as const
export const insightPageSchema = z.enum(INSIGHT_PAGES)

// ── /insights/{page}/ ────────────────────────────────────────────────────────

/** What the card's numbers describe — never a call on the share. */
export const INSIGHT_TONES = ['positive', 'neutral', 'caution'] as const
export const insightToneSchema = z.enum(INSIGHT_TONES)

/**
 * Why a take is being rewritten: never written (`pending`), its facts changed
 * (`new_data`), sessions have passed (`new_session`), a date it calls upcoming
 * has gone by (`date_passed`), or nobody has looked since an edit
 * (`not_checked`). The screen says the same thing for all five.
 */
export const INSIGHT_UPDATING_REASONS = [
  'pending',
  'new_data',
  'new_session',
  'date_passed',
  'not_checked',
] as const
export const insightUpdatingReasonSchema = z.enum(INSIGHT_UPDATING_REASONS)

/**
 * Why there is no take: nothing to describe (`no_data`), the text failed
 * mm-be's advice or number checks (`held_back`), or writing it failed
 * (`failed`). All three read "Not available yet".
 */
export const INSIGHT_UNAVAILABLE_REASONS = ['no_data', 'held_back', 'failed'] as const
export const insightUnavailableReasonSchema = z.enum(INSIGHT_UNAVAILABLE_REASONS)

/**
 * The "Mitra says" box. `featured` names up to three `keyNumbers` ids, most
 * important first. `tone` is null for a word this build does not know.
 */
export const insightCardSchema = z.object({
  headline: z.string().min(1),
  summary: z.string().min(1),
  tone: insightToneSchema.nullable(),
  featured: z.array(z.string().min(1)),
})

/**
 * A number drawn beside the text, computed by mm-be's code (never the model).
 * `display` is ready to print as sent ("Rs 546.00", "4.04%"); `unit` is
 * Rs · Rs crore · % · x · points · count · date. `context` is a reference
 * point ("Sector middle: 1.60"); `asDate` is set when the number IS a date.
 */
export const insightKeyNumberSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  display: z.string().min(1),
  unit: z.string(),
  context: z.string().min(1).nullable(),
  asDate: isoDateSchema.nullable(),
})

/**
 * PROPOSED (`asks/cp1.md` A9) — a brief section's bar chart (the mock's "Profit so far this year
 * vs a year ago"): labelled bars, each with its value and the words it prints. Read only once
 * `insightsService.chartsAvailable` flips; until then every section's `chart` is null.
 */
export const insightSectionChartSchema = z.object({
  kind: z.literal('bars'),
  rows: z
    .array(z.object({ label: z.string().min(1), value: z.number(), display: z.string().min(1) }))
    .min(1),
})

/**
 * One paragraph of the explainer. `keyNumber` is the `keyNumbers` id the
 * paragraph is about — the figure to draw under it — or null. `chart` is the
 * section's own chart where mm-be serves one (proposed; see above): a malformed
 * one costs only the chart.
 */
export const insightSectionSchema = z.object({
  heading: z.string().min(1),
  body: z.string().min(1),
  keyNumber: z.string().min(1).nullable(),
  chart: insightSectionChartSchema.nullable().default(null).catch(null),
})

/** A word the explainer uses ("Words to know"), said plainly. */
export const insightGlossaryItemSchema = z.object({
  term: z.string().min(1),
  plain: z.string().min(1),
})

/**
 * The "Mitra explains" popup's text, sent only to a `part=full` read: a
 * title, a one-line lede, short sections, up to three takeaways ("In short"),
 * a glossary and one caveat (`keepInMind`). A blank item, or a section or
 * glossary row missing either half, is dropped rather than failing the take.
 * A blank title is null, not a reason to drop the rest: the popup falls back
 * to the card's headline. The explainer itself is null only when the service
 * finds nothing usable in it.
 */
export const insightExplainerSchema = z.object({
  title: z.string().min(1).nullable(),
  oneLiner: z.string().min(1).nullable(),
  sections: lenientList(insightSectionSchema, 'insight section'),
  takeaways: lenientList(z.string().min(1), 'insight takeaway'),
  glossary: lenientList(insightGlossaryItemSchema, 'insight glossary'),
  keepInMind: z.string().min(1).nullable(),
})

/**
 * What every state carries. `asOf` is the session or record the take
 * describes (print it as "As of …"); `updatedAt` is when it was written.
 * Either can be null, even on a ready take. `disclaimer` is mm-be's own line
 * — print it as sent, never a copy of it.
 */
const insightBaseSchema = z.object({
  symbol: z.string().min(1),
  page: insightPageSchema,
  asOf: isoDateSchema.nullable(),
  updatedAt: isoDateTimeSchema.nullable(),
  keyNumbers: lenientList(insightKeyNumberSchema, 'insight key number'),
  disclaimer: z.string().min(1),
})

/**
 * One page's take, in one of three states (see the file's rule 1):
 *  - `ready`        the card, the key numbers it features, and the explainer
 *                   — always null from a `part=card` read, and null from a
 *                   `part=full` one whose explainer had nothing usable.
 *  - `updating`     "Mitra is updating this take": no card, no numbers.
 *  - `unavailable`  "Not available yet": no card, no numbers.
 * A company with no takes at all (a fund, a debenture, an index) is not a
 * state here: the read resolves `null` and the screen hides the take.
 */
export const companyInsightSchema = z.discriminatedUnion('status', [
  insightBaseSchema.extend({
    status: z.literal('ready'),
    reason: z.null(),
    card: insightCardSchema,
    explainer: insightExplainerSchema.nullable(),
  }),
  insightBaseSchema.extend({
    status: z.literal('updating'),
    reason: insightUpdatingReasonSchema.nullable(),
    card: z.null(),
    explainer: z.null(),
  }),
  insightBaseSchema.extend({
    status: z.literal('unavailable'),
    reason: insightUnavailableReasonSchema.nullable(),
    card: z.null(),
    explainer: z.null(),
  }),
])

// ── /insights/{page}/feedback/ ───────────────────────────────────────────────

/**
 * Why a reader voted 👎 — mm-be's reasons less `helpful`, which is what a 👍
 * is stored as. `advice` is "Sounds like advice".
 */
export const INSIGHT_FEEDBACK_REASONS = [
  'wrong',
  'advice',
  'confusing',
  'outdated',
  'other',
] as const
export const insightFeedbackReasonSchema = z.enum(INSIGHT_FEEDBACK_REASONS)

/**
 * A vote. A 👎 always says why (mm-be answers 400 without a reason) and a 👍
 * never does, so the type cannot build either mistake. A second vote replaces
 * the reader's first.
 */
export const insightVoteSchema = z.discriminatedUnion('helpful', [
  z.object({ helpful: z.literal(true) }),
  z.object({ helpful: z.literal(false), reason: insightFeedbackReasonSchema }),
])

export type InsightPage = z.infer<typeof insightPageSchema>
export type InsightTone = z.infer<typeof insightToneSchema>
export type InsightUpdatingReason = z.infer<typeof insightUpdatingReasonSchema>
export type InsightUnavailableReason = z.infer<typeof insightUnavailableReasonSchema>
export type InsightCard = z.infer<typeof insightCardSchema>
export type InsightKeyNumber = z.infer<typeof insightKeyNumberSchema>
export type InsightSection = z.infer<typeof insightSectionSchema>
export type InsightGlossaryItem = z.infer<typeof insightGlossaryItemSchema>
export type InsightExplainer = z.infer<typeof insightExplainerSchema>
export type CompanyInsight = z.infer<typeof companyInsightSchema>
export type InsightFeedbackReason = z.infer<typeof insightFeedbackReasonSchema>
export type InsightVote = z.infer<typeof insightVoteSchema>
export type InsightSectionChart = z.infer<typeof insightSectionChartSchema>
